// ============================================================================
// PublicBlockRenderer — renders editor blocks (BuilderElement[]) on the LIVE site.
// Ported 1:1 from the admin BlockPreview so what you see in the Web Editor is
// what publishes. Adds animation support (data-anim + IntersectionObserver).
// ============================================================================
import { useEffect, useRef } from "react";
import type { BuilderElement, AnimationSettings } from "@/lib/cms";
import "@/styles/cms-animations.css";

const DUR: Record<string, string> = { fast: "0.4s", normal: "0.7s", slow: "1.1s" };

function animProps(a?: AnimationSettings) {
  if (!a || a.type === "none") return {};
  return {
    "data-anim": a.type,
    "data-trigger": a.trigger,
    style: {
      "--anim-duration": DUR[a.duration] || "0.7s",
      "--anim-delay": `${a.delay || 0}ms`,
      "--anim-timing": a.timing || "ease",
    } as React.CSSProperties,
  };
}

function Block({ block }: { block: BuilderElement }) {
  const align = block.align || "left";
  const a = animProps(block.animation);

  switch (block.type) {
    case "heading":
      return <h2 {...a} className={`cms-anim text-3xl md:text-4xl font-bold text-gray-900 py-3 text-${align}`}>{block.content}</h2>;
    case "text":
      return <p {...a} className={`cms-anim text-base text-gray-600 leading-relaxed py-2 text-${align}`}>{block.content}</p>;
    case "image":
      return block.src ? <img {...a} src={block.src} alt={block.content || ""} loading="lazy" className="cms-anim w-full rounded-xl object-cover my-3" /> : null;
    case "button":
      return (
        <div {...a} className={`cms-anim flex justify-${align} py-3`}>
          <a href={block.href || "#"} className="inline-block bg-teal-600 text-white text-sm font-semibold px-6 py-3 rounded-lg hover:bg-teal-500 transition-colors">{block.content}</a>
        </div>
      );
    case "divider":
      return <hr className="border-gray-200 my-4" />;
    case "spacer":
      return <div className="h-10" />;
    case "video":
      return block.src ? (
        <div {...a} className="cms-anim aspect-video bg-gray-900 rounded-xl overflow-hidden my-3">
          <iframe src={block.src} className="w-full h-full" allowFullScreen title="Video" />
        </div>
      ) : null;
    case "column":
      return (
        <div {...a} className="cms-anim grid grid-cols-1 md:grid-cols-2 gap-6 py-3">
          {block.children?.map((c) => <Block key={c.id} block={c} />)}
        </div>
      );
    case "form":
      return (
        <form {...a} className="cms-anim bg-gray-50 rounded-2xl border border-gray-200 p-6 my-4" onSubmit={(e) => e.preventDefault()}>
          {block.content && <h3 className="text-lg font-bold text-gray-900 mb-4">{block.content}</h3>}
          <div className="space-y-3">
            {(block.fields || [{ label: "Name", type: "text" }, { label: "Email", type: "email" }, { label: "Message", type: "textarea" }]).map((f, i) => (
              <div key={i}>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">{f.label}</label>
                {f.type === "textarea"
                  ? <textarea rows={4} name={f.label} className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white" placeholder={f.label} />
                  : <input type={f.type} name={f.label} className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white" placeholder={f.label} />}
              </div>
            ))}
          </div>
          <button type="submit" className="mt-4 text-sm bg-teal-600 hover:bg-teal-500 text-white px-5 py-2.5 rounded-lg font-semibold">{block.href || "Submit"}</button>
        </form>
      );
    case "map": {
      const q = block.lat && block.lng ? `${block.lat},${block.lng}` : "Nairobi, Kenya";
      return (
        <div {...a} className="cms-anim aspect-video bg-gray-100 rounded-xl overflow-hidden my-3 border border-gray-200">
          <iframe
            src={`https://www.google.com/maps?q=${encodeURIComponent(q)}&z=${block.zoom || 14}&output=embed`}
            className="w-full h-full border-0" allowFullScreen loading="lazy"
            referrerPolicy="no-referrer-when-downgrade" title={block.content || "Map"}
          />
        </div>
      );
    }
    case "testimonial":
      return (
        <div {...a} className="cms-anim bg-white rounded-2xl border border-gray-200 p-6 my-4 shadow-sm">
          <div className="flex gap-1 mb-3">
            {Array.from({ length: block.rating || 5 }).map((_, i) => <i key={i} className="ri-star-fill text-amber-400 text-sm" />)}
          </div>
          <p className="text-base text-gray-700 italic mb-4">&ldquo;{block.content || ""}&rdquo;</p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold">{(block.author || "G")[0]}</div>
            <div>
              <p className="text-sm font-bold text-gray-900">{block.author || "Guest"}</p>
              <p className="text-xs text-gray-400">{block.role || "Customer"}</p>
            </div>
          </div>
        </div>
      );
    case "social":
      return (
        <div {...a} className="cms-anim flex items-center gap-3 py-3">
          {(block.links || []).map((l, i) => (
            <a key={i} href={l.url} target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 rounded-full bg-gray-100 hover:bg-teal-50 flex items-center justify-center text-gray-600 hover:text-teal-600 transition-colors">
              <i className={`ri-${l.platform}-line`} />
            </a>
          ))}
        </div>
      );
    default:
      return null;
  }
}

export default function PublicBlockRenderer({ blocks }: { blocks: BuilderElement[] }) {
  const ref = useRef<HTMLDivElement>(null);

  // Trigger scroll/load animations.
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const els = Array.from(root.querySelectorAll<HTMLElement>(".cms-anim[data-anim]"));
    const load = els.filter((e) => e.dataset.trigger !== "scroll");
    load.forEach((e) => e.classList.add("cms-anim-run"));
    const scroll = els.filter((e) => e.dataset.trigger === "scroll");
    if (!scroll.length) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add("cms-anim-run"); io.unobserve(en.target); } }),
      { threshold: 0.15 }
    );
    scroll.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, [blocks]);

  return (
    <div ref={ref} className="cms-content max-w-5xl mx-auto px-4 md:px-6 py-8">
      {blocks.map((b) => <Block key={b.id} block={b} />)}
    </div>
  );
}
